import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { DialogService } from "@progress/kendo-angular-dialog";
import { combineLatest, forkJoin, Observable, of } from "rxjs";
import { filter, first, map, mergeMap, switchMap } from "rxjs/operators";
import { I18nService } from "src/app/i18n/services";
import {
  AbstractUserService,
  Circulation,
  IDefaultStepPayloadData,
  IPerson,
  IUser,
  Step
} from "src/app/interfaces";
import { NotificationFacade } from "src/app/notifications/services/notification.facade";
import { NavigationService } from "src/app/services";
import { CirculationFacade } from "src/app/services/circulation.facade";
import { PersonFacade } from "src/app/services/person.facade";
import { StepFacade } from "src/app/step/services/step.facade";
import { LogClass, Logger } from "../../../../logger";
import { STEPTYPE_MAIL } from "../../constants";

@Component({
    selector: "circ-create",
    templateUrl: "./create.component.html",
    styleUrls: ["./create.component.scss"],
    standalone: false
})
@LogClass({ source: "stepMail/CreateComponent" })
export class CreateComponent implements OnInit {
  private logger: Logger;

  public form: FormGroup;
  public saving = false;

  /** True if updating a existing step */
  public existingStep = false;

  constructor(
    private circulationFacade: CirculationFacade,
    private navigate: NavigationService,
    private userService: AbstractUserService,
    private stepFacade: StepFacade,
    private personFacade: PersonFacade,
    private i18nService: I18nService,
    private dialogService: DialogService,
    private notification: NotificationFacade
  ) {
    this.logger.debug("ctor");
    this.createForm();
  }
  ngOnInit(): void {
    this.stepFacade
      .getCurrentStep()
      .pipe(
        filter((s) => !!s),
        mergeMap((step) => {
          const data = step.props.payload.data as IDefaultStepPayloadData;
          this.form.setValue({
            title: data?.title,
            comment: data?.comment || "",
            responsible: []
          });
          return this.personFacade.getPersonByLogins(
            step.props.tasks.assignees.map((x) => x.user.login)
          );
        }),
        map((persons) =>
          this.form.patchValue({
            responsible: persons
          })
        ),
        first()
      )
      .subscribe(() => {
        this.logger.info("Found Step");
        this.existingStep = true;
      });
  }

  public abort(): void {
    this.navigateToCurrentCirculation();
  }

  private navigateToCurrentCirculation(): void {
    this.circulationFacade
      .getCurrentCirculation()
      .pipe(
        map((circ) => circ.id),
        first()
      )
      .subscribe((circId) => this.navigate.toCirculation(circId));
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    this.saving = true;
    this.logger.info("saving");

    const logins: string[] = this.form.value.responsible.map(
      (x: IPerson) => x.login
    );
    const user$ = this.userService.getUsers(logins);
    const circ$ = this.circulationFacade.getCurrentCirculation();

    combineLatest([circ$, user$])
      .pipe(
        switchMap(([circ, users]) =>
          this.existingStep
            ? this.createUpdateStep(circ, users)
            : this.createNewStep(circ, users)
        ),
        switchMap((step) => this.stepFacade.save(step)),
        first()
      )
      .subscribe({
        next: (ok) => {
          this.logger.info("saved:" + ok);

          this.navigateToCurrentCirculation();
        },
        error: (err) => {
          this.logger.info("error saving step: " + JSON.stringify(err));

          this.notification.showError(
            "Error while saving this step. Please ask your Administrator for help." +
              err.error.message
          );

          this.navigateToCurrentCirculation();
        }
      });
  }

  private createUpdateStep(
    circ: Circulation,
    users: IUser[]
  ): Observable<Step> {
    return this.stepFacade.getCurrentStep().pipe(
      first(),
      map(
        (step) =>
          new Step(
            {
              ...step.props,
              dueDate: null,
              payload: {
                ...step.props.payload,
                data: {
                  ...step.props.payload.data,
                  title: this.form.value.title,
                  comment: this.form.value.comment
                }
              },
              tasks: {
                ...step.props.tasks,
                assignees: users.map((u) => {
                  return {
                    id: 0,
                    user: u
                  };
                })
              }
            },
            step.id
          )
      )
    );
  }
  private createNewStep(circ: Circulation, users: IUser[]): Observable<Step> {
    return of(
      new Step({
        id: 0,
        stepNo: 0,
        tasks: {
          firstResponseApplies: false,
          assignees: users.map((u) => {
            return {
              id: 0,
              user: u
            };
          })
        },
        circulationId: circ.id,
        status: "Waiting",
        payload: {
          stepType: STEPTYPE_MAIL,
          data: {
            title: this.form.value.title,
            comment: this.form.value.comment
          }
        }
      })
    );
  }

  public delete(): void {
    forkJoin({
      step_edit_deleteDlg_title: this.i18nService.get(
        "step_edit_deleteDlg_title"
      ),
      step_edit_deleteDlg_text: this.i18nService.get(
        "step_edit_deleteDlg_text"
      ),
      step_edit_deleteDlg_yes: this.i18nService.get("step_edit_deleteDlg_yes"),
      step_edit_deleteDlg_no: this.i18nService.get("step_edit_deleteDlg_no")
    })
      .pipe(
        mergeMap((translations) => {
          return this.dialogService.open({
            title: translations["step_edit_deleteDlg_title"],
            content: translations["step_edit_deleteDlg_text"],
            actions: [
              { text: translations["step_edit_deleteDlg_yes"], doDelete: true },
              { text: translations["step_edit_deleteDlg_no"], primary: true }
            ],
            width: 450,
            height: 200,
            minWidth: 250
          }).result;
        })
        // eslint-disable-next-line prettier/prettier
      )
      .subscribe((result) => {
        if (!(result as { doDelete: boolean }).doDelete) {
          return;
        }
        this.logger.info("deleting step");
        this.stepFacade
          .getCurrentStep()
          .pipe(
            first(), //To stop after delete
            switchMap((step) => this.stepFacade.delete(step.props)),
            first()
          )
          .subscribe({
            next: (id) => {
              this.logger.info("deleted!");

              this.navigate.toCirculation(id);
            },
            error: (err) => {
              this.logger.info("error deleting step: " + JSON.stringify(err));

              this.notification.showError(
                "Error while deleting this step. Please ask your Administrator for help." +
                  err.error.message
              );

              this.navigateToCurrentCirculation();
            }
          });
      });
  }

  private createForm() {
    this.form = new FormGroup({
      title: new FormControl("", [Validators.required]),
      responsible: new FormControl([], [Validators.required]),
      comment: new FormControl("")
    });
  }
}
